import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useCollectorAuth } from "@/context/CollectorAuthContext";

/**
 * Guards every collector-facing dashboard route.
 *
 * Also enforces the Universal Collector Onboarding gate: once a
 * collector has started onboarding (`status === 'in_progress'`) they
 * are BOUNCED back into the flow if they try to reach the dashboard.
 * Onboarding is mandatory once started — there is no skip.
 *
 * Legacy accounts (`status === 'not_started'` OR no onboarding doc)
 * are NEVER forced through the flow — they were created before Phase A
 * and can access the dashboard normally.
 */
export default function CollectorProtectedRoute({ children }) {
  const { collector, loading } = useCollectorAuth();
  const location = useLocation();

  if (loading) return <div className="p-10 text-center text-[var(--smoke)]">Loading…</div>;
  if (!collector) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace state={{ from: location.pathname }} />;
  }

  const ob = collector.onboarding;
  if (ob && ob.status === "in_progress") {
    // Deep-link into the collector's current step. If we're already
    // inside /onboarding/* the OnboardingLayout owns navigation, so
    // this branch never fires there (the /onboarding/* routes are
    // NOT wrapped by CollectorProtectedRoute).
    // Signup redesign — the flow is now 3 steps (profile, albums,
    // congrats). Legacy step values from pre-redesign sessions are
    // mapped forward so no collector gets stranded on a removed step.
    const raw = ob.current_step || "profile";
    const legacyMap = { welcome: "profile", completion: "albums", help: "albums" };
    const step = legacyMap[raw] || raw;
    return <Navigate to={`/onboarding/${step}`} replace />;
  }

  return children;
}
